import { AxiosError } from "axios";
import _unionBy from "lodash/unionBy";
import {
  Button,
  debounce,
  DropdownInput,
  getErrorPopupTexts,
  IFilterValues,
  IOption,
  IUserFilters,
  Search,
} from "component-library";
import { TagminLogoImage } from "assets";
import MakeSuggestionsFilterControl from "components/MakeSuggestionsFilterControl/MakeSuggestionsFilterControl";
import { ISuggestionsViewTexts } from "components/SuggestionsViewAgent/SuggestionsViewAgent.helpers";
import { getPatch } from "components/SuggestionsViewPerformer/SuggestionsViewPerformer.helpers";
import getErrorPopupType from "helpers/getErrorPopupType";
import {
  FilterType,
  IFilterModel,
  WhichCVFilterValues,
  WhichCVType,
} from "hoc/withMakeSuggestionsFilter/withMakeSuggestionsFilter.config";
import { FilterModelToMakeSuggestionsFilterParamsConverter } from "hoc/withMakeSuggestionsFilter/withMakeSuggestionsFilter.converters";
import {
  filterEditedData,
  findFilteredResults,
  getCreatedData,
} from "hoc/withMakeSuggestionsFilter/withMakeSuggestionsFilter.helpers";
import { i18n, Trans } from "i18n";
import { IMakeSuggestionsFilterParams } from "models/api";
import { IPaginatedData } from "models/common";
import {
  IMakeSuggestionsPerformer,
  IMakeSuggestionsPerformersStatsResponse,
} from "models/suggestion";
import { INPUT_NAMES } from "pages/JobsPage/JobsPage.helpers";
import React from "react";
import { connect } from "react-redux";
import ResizeObserver from "react-resize-observer";
import suggestionsService from "services/api/suggestions/suggestionsService";
import {
  GetPerformers,
  GetPerformersStats,
  IPatchSuggestionEditMap,
  ISetSuggestionRequestData,
  SetSuggestions,
} from "services/api/suggestions/suggestionsService.types";
import { IFacetGroup } from "shared/models/facet";
import agentsService, {
  GetAgencies,
} from "shared/services/api/agents/agentsService";
import { IApplicationState } from "store";
import { FacetStoreKey } from "store/facet/facet.types";
import { ITranslationState } from "store/translation/translation.types";
import { HidePopup, PopupType, ShowPopup } from "store/ui/ui.types";
import MakeSuggestionsGrid from "./MakeSuggestionsGrid/MakeSuggestionsGrid";
import "./MakeSuggestionsView.scss";
import { dataTestIds } from "data-testids";

export const SEARCH_MAX_LENGTH = 55;

export type ChangeFilters = (
  values: Partial<IFilterModel>,
  callback?: (updatedModel: IFilterModel) => void
) => void;

export interface IMakeSuggestionsViewStoreProps {
  pageTranslationLocale: string;
  genders: IFacetGroup[];
  ethnicBackgroundFacet: IFacetGroup[];
  heritageAndEthnicityFacet: IFacetGroup[];
  appearances: IFacetGroup[];
  translations: ITranslationState;
  facetLocale: string;
  locale: string;
  getPerformers: GetPerformers;
  setSuggestions: SetSuggestions;
  getAgencies: GetAgencies;
  getPerformersStats: GetPerformersStats;
}
interface IMakeSuggestionsViewProps {
  filters: IUserFilters<IFilterValues>;
  texts: ISuggestionsViewTexts;
  onToggleView: () => void;
  hidePopup: HidePopup;
  showPopup: ShowPopup;
  handleError: (e: AxiosError, message?: string) => void;
  roleId: string;
  breakdownId: string;
  appearanceIds: number[];
  location: IOption<string>[];
  pageTranslationLocale: string;
  facetLocale: string;
  appearances: IFacetGroup[];
  genders: IFacetGroup[];
  getPerformers: GetPerformers;
  getPerformersStats: GetPerformersStats;
  getAgencies: GetAgencies;
  translations: ITranslationState;
  ethnicBackgroundFacet: IFacetGroup[];
  heritageAndEthnicityFacet: IFacetGroup[];
  locale: string;
  setSuggestions: SetSuggestions;
}
interface IMakeSuggestionsViewState {
  values: Pick<
    IFilterModel,
    | FilterType.TextFilter
    | FilterType.PageNumber
    | FilterType.WhichCV
    | FilterType.PageSize
  >;
  isLoading: boolean;
  paginatedCards: IPaginatedData<IMakeSuggestionsPerformer>;
  suggestedPerformers: IMakeSuggestionsPerformer[];
  stats: IMakeSuggestionsPerformersStatsResponse;
  wrapperWidth: number;
}

export class MakeSuggestionsView extends React.Component<
  IMakeSuggestionsViewProps,
  IMakeSuggestionsViewState
> {
  private readonly wrapperRef = React.createRef<HTMLDivElement>();
  private readonly defaultPageNumber = 1;
  private readonly defaultPageSize = 36;
  public abortController = new AbortController();
  public agencyRequestAbortController = new AbortController();
  private removedSuggestions: string[] = [];
  private editedSuggestions: IPatchSuggestionEditMap = {};

  constructor(props: IMakeSuggestionsViewProps) {
    super(props);

    this.state = {
      values: {
        [FilterType.TextFilter]: "",
        [FilterType.PageNumber]: this.defaultPageNumber,
        [FilterType.WhichCV]: WhichCVType.AllProfiles,
        [FilterType.PageSize]: this.defaultPageSize,
      },
      isLoading: false,
      paginatedCards: {
        count: 0,
        items: [],
      },
      suggestedPerformers: [],
      stats: { appearances: [], heritageAndEthnicity: [] },
      wrapperWidth: 0,
    };
  }

  public componentDidMount(): void {
    void this.fetchData();
  }

  public componentWillUnmount(): void {
    this.abortController.abort?.();
    this.agencyRequestAbortController.abort?.();
  }

  public render() {
    const filters = this.getFilters();

    return (
      <section className="c-make-suggestions" ref={this.wrapperRef}>
        <ResizeObserver onResize={this.adaptToWindowDebounced} />
        <div className="g-bg-default c-make-suggestions__content">
          <span className="c-make-suggestions__suggest-clients-label">
            {this.props.texts.suggestClients}
          </span>

          {this.renderFilterControls()}

          <MakeSuggestionsFilterControl
            stats={this.state.stats}
            filters={filters}
            loadCards={this.loadCards}
            withSuggestions={true}
          />

          <MakeSuggestionsGrid
            performers={this.state.paginatedCards.items}
            pageNumber={this.state.values[FilterType.PageNumber]}
            itemsCount={this.state.paginatedCards.count}
            itemsPerPage={this.state.values[FilterType.PageSize]}
            isPageLoading={filters.isLoading}
            updatePageNumber={this.updatePageNumber}
            texts={this.props.texts}
            onSelectChange={this.onSelectChange}
            onCommentChange={this.onCommentChange}
            onPhotoSelect={this.onPhotoSelect}
            suggestedPerformers={this.getSuggestedPerformers()}
            isLoading={this.state.isLoading}
            wrapperWidth={this.state.wrapperWidth}
          />
        </div>
        <section className="c-make-suggestions__buttons-set g-col-lg-12 g-inner-grid">
          <div className="g-col-lg-4 g-col-md-1" />
          <div className="c-make-suggestions__selected-counter-outer g-col-lg-2 g-col-md-3">
            <span className="c-make-suggestions__selected-counter">
              {this.props.texts.cvCount.replace(
                "X",
                this.state.suggestedPerformers.length.toString()
              )}
            </span>
          </div>
          <div className="g-col-lg-3 g-col-md-4">
            <Button
              type="secondary"
              text={this.props.texts.cancel}
              onClick={this.props.onToggleView}
            />
          </div>
          <div className="g-col-lg-3 g-col-md-4">
            <Button
              type="primary"
              text={this.props.texts.submit}
              onClick={this.onSubmitSuggestion}
              disabled={this.state.isLoading}
              data-testid={
                dataTestIds.nuJobsApp["MakeSuggestionsView.Suggestion.Submit"]
              }
            />
          </div>
        </section>
      </section>
    );
  }

  private readonly getSuggestedPerformers = () => {
    return _unionBy(
      this.state.suggestedPerformers,
      this.state.paginatedCards.items.filter((item) => item.suggestion?.id),
      "artistRef"
    );
  };

  private readonly fetchData = async () => {
    await this.loadCards(true);
  };

  private readonly getFilters = () => {
    return this.props.filters;
  };

  private renderFilterControls = () => {
    return (
      <div className="c-make-suggestions__filter-controls">
        <div className="g-content-area c-make-suggestions__search-panel items-end">
          <div className="g-col-lg-4">
            <Search
              placeholder={this.props.texts.searchPerformer}
              maxLength={SEARCH_MAX_LENGTH}
              onSearch={this.handleSearch}
              onClear={this.handleSearchClear}
            />
          </div>
          <div className="g-col-lg-3">
            <DropdownInput<string>
              id="makeSuggestionsOptions"
              name={INPUT_NAMES.showType}
              variant="select"
              options={this.props.texts.cvOptions}
              selected={this.props.texts.cvOptions[0]}
              value={this.state.values[FilterType.WhichCV]}
              optionSelected={this.handleWhichCVChange}
            />
          </div>
          <div className="g-col-lg-5">
            <div className="flex items-center justify-between border border-color-accent-one bg-blue-mist-0 py-[5px] pl-[15px] pr-[5px] text-[14px] leading-[18px]">
              <div>
                <div className="font-semibold">
                  {i18n.t("agent:tagminAvailabilityCalendar.banner.title")}
                </div>
                <div>
                  <Trans
                    i18nKey="agent:tagminAvailabilityCalendar.banner.text"
                    components={{
                      a: (
                        <a
                          href="https://www.spotlight.com/spotlight/availability-feed/"
                          target="_blank"
                          rel="noreferrer"
                          aria-label=""
                        />
                      ),
                    }}
                  />
                </div>
              </div>
              <img
                className="h-[38px]"
                src={TagminLogoImage}
                alt={i18n.t("agent:tagminAvailabilityCalendar.alt.logo")}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  private updatePageNumber = (page: number) => {
    this.changeFilters({ [FilterType.PageNumber]: page });
  };

  private onSelectChange = (
    item: IMakeSuggestionsPerformer,
    isSelected: boolean
  ) => {
    this.handleUpdateSuggestedPerformers(item, isSelected);
  };

  private onCommentChange = (
    item: IMakeSuggestionsPerformer,
    comment: string
  ) => {
    const suggestion = item.suggestion!;
    const changes = suggestion.id
      ? getPatch(suggestion.photo, comment, suggestion.id)
      : undefined;

    this.handleUpdateSuggestedPerformers(
      { ...item, suggestion: { ...suggestion, comment } },
      true,
      changes
    );
  };

  private readonly onPhotoSelect = (
    item: IMakeSuggestionsPerformer,
    photo: string
  ) => {
    const suggestion = item.suggestion!;
    const changes = suggestion.id
      ? getPatch(photo, suggestion.comment, suggestion.id)
      : undefined;

    this.handleUpdateSuggestedPerformers(
      { ...item, suggestion: { ...suggestion, photo } },
      true,
      changes
    );
  };

  private handleWhichCVChange = (option: IOption<WhichCVType>) => {
    this.changeFilters({ [FilterType.WhichCV]: option.value });
  };

  private handleSearch = (search?: string) => {
    this.changeFilters({ [FilterType.TextFilter]: search });
  };

  private handleSearchClear = () => {
    this.handleSearch();
  };

  private onSubmitSuggestion = async () => {
    const { setSuggestions, roleId, breakdownId, onToggleView } = this.props;

    this.setState({ isLoading: true });

    try {
      const data = this.getMakeSuggestionsData();

      await setSuggestions(breakdownId, roleId, data);

      this.setState({ isLoading: false });
      onToggleView();
    } catch (e) {
      this.setState({ isLoading: false }, () =>
        this.props.handleError(e, "Submit suggestions")
      );
    }
  };

  private readonly handleUpdateSuggestedPerformers = (
    performer: IMakeSuggestionsPerformer,
    isSelected: boolean,
    changes?: IPatchSuggestionEditMap
  ) => {
    const suggestionId = performer.suggestion?.id;
    const newList = this.state.suggestedPerformers.filter(
      (item) => item.artistRef !== performer.artistRef
    );

    if (changes) {
      this.editedSuggestions = {
        ...this.editedSuggestions,
        ...changes,
      };
    }

    if (isSelected) {
      const newPerformer = {
        ...performer,
        suggestion: {
          id: performer.suggestion?.id,
          comment: performer.suggestion?.comment || "",
          photo: performer.suggestion?.photo || performer.mainPhoto.id,
        },
      };

      this.removedSuggestions = this.removedSuggestions.filter(
        (id) => id !== suggestionId
      );
      this.setState({
        suggestedPerformers: [...newList, newPerformer],
      });
    } else {
      this.removedSuggestions = suggestionId
        ? [...this.removedSuggestions, suggestionId]
        : this.removedSuggestions;
      this.setState({
        suggestedPerformers: newList,
      });
    }
  };

  private readonly changeFilters: ChangeFilters = (values, callback) => {
    const stateValues = { ...this.state.values };

    const updatedValues = {
      ...stateValues,
      [FilterType.PageNumber]: this.defaultPageNumber,
      ...values,
    };

    this.setState({ values: updatedValues }, () => {
      callback?.(updatedValues as IFilterModel);
      void this.loadCards();
    });
  };

  private readonly filterCardsLocally = (
    filters: IMakeSuggestionsFilterParams
  ) => {
    const stateSuggestedPerformers = [...this.state.suggestedPerformers];

    const performers = findFilteredResults(stateSuggestedPerformers, filters);

    this.setState({
      paginatedCards: {
        items: performers.slice(filters.skip, filters.skip + filters.take! + 1),
        count: performers.length,
      },
    });
  };

  private readonly loadCards = async (withSuggestions: boolean = false) => {
    const { suggestedPerformers } = this.state;

    const filterValues = this.getFilters().values;

    const values: IFilterModel = {
      ...filterValues,
      ...this.state.values,
      playingAge: {
        maxValue: filterValues.playingAge.upperRangeValue,
        minValue: filterValues.playingAge.lowerRangeValue,
      },
      ethnicBackground: {
        ...filterValues.heritageEthnicityAndBackground.ethnicBackground,
      },
      heritageAndEthnicity: {
        ...filterValues.heritageEthnicityAndBackground.heritageAndEthnicity,
      },
      heritageAndEthnicityFilterMode:
        filterValues.heritageEthnicityAndBackground
          .heritageAndEthnicityFilterMode,
      heritageAndEthnicityInfo:
        filterValues.heritageEthnicityAndBackground.heritageAndEthnicityInfo,
    };

    const { getPerformers, getPerformersStats, roleId } = this.props;

    this.setState({ isLoading: true });

    try {
      const converter = new FilterModelToMakeSuggestionsFilterParamsConverter();
      const filters = converter.convert(values);

      if (this.state.values.whichCV === WhichCVType.CurrentSuggestions) {
        this.filterCardsLocally(filters);
      } else {
        const [performers, suggestions, stats] = await Promise.all([
          getPerformers(filters, roleId),
          withSuggestions
            ? getPerformers(
                { skip: 0, whichCV: WhichCVFilterValues.Suggestions },
                roleId
              )
            : Promise.resolve(undefined),
          getPerformersStats(filters, roleId),
        ]);

        this.setState({
          paginatedCards: {
            items: performers.items,
            count: performers.total,
          },
          suggestedPerformers: _unionBy(
            suggestedPerformers,
            suggestions?.items || [],
            "artistRef"
          ),
          stats: stats,
        });
      }
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  readonly handleError = (e: AxiosError) => {
    this.props.showPopup({
      traceID: e?.response?.headers?.["x-trace-identifier"],
      type: PopupType.ErrorPopup,
      data: {
        ...getErrorPopupTexts()[getErrorPopupType()],
        hidePopup: this.props.hidePopup,
      },
    });
  };

  private readonly getMakeSuggestionsData = (): ISetSuggestionRequestData => {
    const edited = { ...this.editedSuggestions };

    return {
      created: getCreatedData(this.state.suggestedPerformers),
      deleted: this.removedSuggestions,
      edited: filterEditedData(this.removedSuggestions, edited),
    };
  };

  private adaptToWindow = () => {
    this.setState({
      wrapperWidth: this.wrapperRef.current?.scrollWidth || 0,
    });
  };

  private adaptToWindowDebounced = debounce(this.adaptToWindow, 150, false);
}

const mapStateToProps = (
  state: IApplicationState
): IMakeSuggestionsViewStoreProps => {
  return {
    locale: state.ui.locale,
    facetLocale: state.facet.locale,
    pageTranslationLocale: state.translation.pageLocale,
    getPerformers: suggestionsService.getPerformers,
    getAgencies: agentsService.getAgencies,
    genders: state.facet[FacetStoreKey.Gender]!,
    ethnicBackgroundFacet: state.facet[FacetStoreKey.EthnicBackground]!,
    heritageAndEthnicityFacet: state.facet[FacetStoreKey.HeritageAndEthnicity]!,
    setSuggestions: suggestionsService.setSuggestions,
    translations: state.translation,
    appearances: state.facet[FacetStoreKey.Appearance]!,
    getPerformersStats: suggestionsService.getPerformerStats,
  };
};

export default connect(mapStateToProps)(MakeSuggestionsView);
